// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { formatDate } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import moment, { Moment } from 'moment';
import { SettingsService } from '../services/settings.service';

@Pipe({
  name: 'localizedDate',
  pure: false,
  standalone: true,
})
export class LocalizedDatePipe implements PipeTransform {
  constructor(private settingsController: SettingsService) {}

  transform(
    date: string | Date | null,
    format: 'longFormat' | 'shortFormat' = 'longFormat',
    fallback = '-'
  ): string {
    const currentDateFormat = this.settingsController.dateFormat$?.getValue();
    if (!currentDateFormat) {
      return fallback;
    }
    try {
      if (!date || !isValidDate(date)) {
        return fallback;
      }

      const dateStringResult = formatDate(
        date,
        currentDateFormat[format],
        'en-US'
      );
      return dateStringResult;
    } catch {
      if (fallback) {
        return fallback;
      } else {
        throw new Error(`invalid date passed to LocalizedDatePipe ${date}`);
      }
    }
  }
}

@Pipe({
  name: 'localizedTime',
  pure: false,
  standalone: true,
})
export class LocalizedTimePipe implements PipeTransform {
  transform(date: string | Date | null, fallback = '-'): string {
    try {
      return date && isValidDate(date)
        ? formatDate(date, 'shortTime', 'en-US')
        : fallback;
    } catch {
      if (fallback) {
        return fallback;
      } else {
        throw new Error(`invalid date passed to LocalizedTimePipe ${date}`);
      }
    }
  }
}

@Pipe({
  name: 'timeFromNow',
  pure: false,
  standalone: true,
})
export class TimeFromNowPipe implements PipeTransform {
  transform(date: string | Date | Moment | null, fallback = '-'): string {
    try {
      return isValidDate(date) ? moment(date).fromNow() : fallback;
    } catch {
      if (fallback) {
        return fallback;
      } else {
        throw new Error(`invalid date passed to LocalizedDatePipe ${date}`);
      }
    }
  }
}

// to detect invalid dates
// please fix the api coilService.getCoilStatus to return an ABSOLUTE!!!! date
// also dont send 0 in case there is no date

const VALID_DATE_CUTOFF = moment('1000-01-01', 'YYYY-MM-DD', true);

export function isValidDate(date: string | Date | Moment | null): boolean {
  if (!date) {
    return false;
  }
  if (moment.isMoment(date)) {
    return true;
  }

  // Parse date with validation for ISO 8601 and fallback for flexible parsing
  const m = moment(date, moment.ISO_8601, true);

  // Additional check if the date is not in ISO format
  if (!m.isValid()) {
    return false;
  }

  return date !== '0001-01-01T00:00:00Z' && m.isAfter(VALID_DATE_CUTOFF);
}
